import {
  type CollisionDetection,
  DndContext,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import classNames from 'classnames';
import {
  type CSSProperties,
  type RefObject,
  useCallback,
  useState,
} from 'react';

import { useCustomPageStreamStatus } from '../../../global-ai-assistant/use-custom-page-stream-status';
import { useGridLayout } from '../../hooks/useGridLayout';
import { Chart } from '../Chart/ChartRoot/Chart';
import { ChartBuilderPanel } from '../ChartConfig/ChartBuilderPanel';
import { useResolvedItems } from '../DataResolver/DataResolver';
import gutterStyles from '../grid-gutter.module.css';
import { DEFAULT_GRID_ROWS } from '../PageConfigProvider/constants';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import { ChartClipboardContextMenu } from './components/ChartClipboardContextMenu';
import { CustomDashGenerationOverlay } from './components/CustomDashGenerationOverlay';
import { GhostPlaceholders } from './components/GhostPlaceholders';
import { Draggable } from './Draggable';
import styles from './EditableGrid.module.css';
import { EditGrid } from './EditGrid';
import { GridActionButton } from './GridActionButton';
import { useGrid } from './GridProvider';
import { useEditableGrid } from './hooks/useEditableGrid';
import { calculateGhosts } from './utils/ghost-layout-generator';

const customCollisionDetectionAlgorithm: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }
  return rectIntersection(args);
};

interface DashboardEditableGridProps {
  onBuilderOpenChange?: (isOpen: boolean) => void;
  gridContainerRef: RefObject<HTMLDivElement>;
}

export const DashboardEditableGrid = ({
  onBuilderOpenChange,
  gridContainerRef,
}: DashboardEditableGridProps) => {
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 10 },
  });
  const sensors = useSensors(pointerSensor);

  const { gridItems, grid, edgeDragging, itemDragging } = useGrid();
  const { primaryFilter, dispatch } = useTabContentConfig();
  const resolvedItems = useResolvedItems();
  const pageStreamStatus = useCustomPageStreamStatus();
  const gridLayout = useGridLayout({
    isReport: false,
    gridRows: grid.rows,
    gridColumns: grid.columns,
  });

  const {
    openConfigId,
    closeConfig,
    selectedIds,
    clearSelection,
    handleChartClick,
    filteredGridItems,
    selectedItem,
    handleAddItem,
    handleDragStart,
    handleDragEnd,
    clipboardContextMenu,
    closeClipboardContextMenu,
    handleClipboardContextMenuAction,
    handleChartContextMenu,
    handleGhostContextMenu,
    handleGhostMouseEnter,
    handleGhostMouseLeave,
  } = useEditableGrid({ onBuilderOpenChange });

  const [dragTransform, setDragTransform] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const onDragEnd = useCallback(
    (event: Parameters<typeof handleDragEnd>[0]) => {
      handleDragEnd(event);
      setDragTransform(null);
    },
    [handleDragEnd]
  );

  const ghostPlaceholders = calculateGhosts({
    items: gridItems,
    grid: { rows: grid.rows, columns: grid.columns },
    isReport: false,
  });

  const addRowSize = DEFAULT_GRID_ROWS / 2;
  const handleAddRows = () => {
    dispatch({ type: 'ADD_ROWS', payload: { rows: addRowSize } });
  };

  const maxRenderedRow = filteredGridItems.reduce(
    (max, item) => Math.max(max, item.position.y + item.position.height),
    0
  );
  const showRemoveRowsButton =
    grid?.rows && grid?.rows > Math.max(maxRenderedRow, DEFAULT_GRID_ROWS);
  const handleRemoveRows = () => {
    const excessRows = grid?.rows - Math.max(maxRenderedRow, DEFAULT_GRID_ROWS);
    if (excessRows <= 0) return;
    if (excessRows < addRowSize) {
      dispatch({ type: 'REMOVE_ROWS', payload: { rows: excessRows } });
    } else {
      dispatch({ type: 'REMOVE_ROWS', payload: { rows: addRowSize } });
    }
  };

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      collisionDetection={customCollisionDetectionAlgorithm}
      modifiers={[restrictToParentElement]}
      sensors={sensors}
      autoScroll={false}
    >
      {(itemDragging || edgeDragging) && (
        <EditGrid
          columns={grid.columns}
          rows={grid.rows}
          width={grid.width}
          height={grid.height}
          paddingLeft={grid.paddingLeft}
          paddingTop={grid.paddingTop}
          trackWidth={grid.trackWidth}
          trackHeight={grid.trackHeight}
          columnGap={grid.columnGap}
          rowGap={grid.rowGap}
        />
      )}
      <div
        id="editable-grid-container"
        ref={gridContainerRef}
        className={classNames(
          styles.grid,
          styles.dashboardEditGridRoot,
          styles.dashboardEditGrid,
          gutterStyles.lattice
        )}
        style={
          {
            gridTemplateRows: gridLayout.styles.gridTemplateRows,
            gridTemplateColumns: gridLayout.styles.gridTemplateColumns,
          } as CSSProperties
        }
      >
        <GhostPlaceholders
          ghosts={ghostPlaceholders}
          onAddItem={handleAddItem}
          onContextMenu={handleGhostContextMenu}
          onMouseEnter={handleGhostMouseEnter}
          onMouseLeave={handleGhostMouseLeave}
        />
        {pageStreamStatus.isActive && pageStreamStatus.mode !== null && (
          <CustomDashGenerationOverlay
            mode={pageStreamStatus.mode}
            message={pageStreamStatus.message}
            hasExistingItems={gridItems.length > 0}
          />
        )}
        {filteredGridItems.map((item) => (
          <Draggable
            id={item.id}
            key={item.id}
            item={item}
            focused={item.id === openConfigId}
            selected={selectedIds.has(item.id)}
            dragTransform={dragTransform}
            setDragTransform={setDragTransform}
            selectedIds={selectedIds}
            onContextMenu={(event) => handleChartContextMenu(item, event)}
            style={{
              gridColumn: `${item.position.x + 1} / span ${item.position.width}`,
              gridRow: `${item.position.y + 1} / span ${item.position.height}`,
            }}
          >
            <Chart
              item={item}
              headerSize={
                item.position.height / grid.rows <= 0.25 ? 'sm' : 'md'
              }
              primaryFilter={primaryFilter}
              data={resolvedItems[item.id]}
              onClick={(e) => handleChartClick(item.id, e)}
              variant="default"
            />
          </Draggable>
        ))}
        <ChartBuilderPanel
          isOpen={!!openConfigId}
          item={selectedItem}
          selectedIds={selectedIds}
          clearSelection={clearSelection}
          onClose={closeConfig}
        />
      </div>
      <ChartClipboardContextMenu
        menu={clipboardContextMenu}
        onAction={handleClipboardContextMenuAction}
        onClose={closeClipboardContextMenu}
      />
      <div className={styles.gridActionBar}>
        <GridActionButton
          label="+ Add Rows"
          onClick={handleAddRows}
          additionalActions={
            showRemoveRowsButton
              ? [
                  {
                    label: '- Remove Rows',
                    onClick: handleRemoveRows,
                    isDisabled: !showRemoveRowsButton,
                  },
                ]
              : undefined
          }
          menuButtonProps={{
            'aria-label': 'More options',
          }}
        />
      </div>
    </DndContext>
  );
};
